'use client'

import { useEffect } from 'react'
import { useThemeStore } from '@/lib/stores/theme-store'
import { getFaviconFromConfig } from '@/lib/theme-helpers'

/**
 * Precarrega assets críticos do tema (imagens de background, etc.)
 * 
 * Funciona em conjunto com o novo sistema de cache:
 * - Lê o tema do store (que está sincronizado com theme-init.js)
 * - Precarrega imagens para evitar flash de conteúdo
 */
export function ThemePreloader() {
  const { currentTheme } = useThemeStore()

  useEffect(() => {
    if (typeof window === 'undefined' || !currentTheme) return

    // Coletar todas as URLs de imagem do tema
    const imageUrls: string[] = []

    // Background do login
    const loginBg = currentTheme.login?.layout?.body?.image
    if (loginBg) imageUrls.push(loginBg)

    // Background do home
    const homeBg = currentTheme.home?.layout?.body?.image
    if (homeBg) imageUrls.push(homeBg)

    // Logo
    const logo = currentTheme.logo
    if (logo) imageUrls.push(logo)

    const favicon = getFaviconFromConfig(currentTheme)
    if (favicon) imageUrls.push(favicon)

    // Slides do banner hero (quando separados da vitrine do login)
    const heroSlides = currentTheme.home?.components?.banner?.slideImages
    if (heroSlides && Array.isArray(heroSlides)) {
      heroSlides.forEach((u) => {
        if (typeof u === "string" && u.trim()) imageUrls.push(u)
      })
    }

    // Banners do modelVideo (vitrine do login)
    const banners = currentTheme.home?.branding?.modelVideo?.banners
    if (banners && Array.isArray(banners)) {
      banners.forEach(banner => {
        if (banner) imageUrls.push(banner)
      })
    }

    // Precarregar cada imagem única
    const uniqueUrls = [...new Set(imageUrls)]

    uniqueUrls.forEach(url => {
      const existingPreload = document.querySelector(`link[rel="preload"][href="${url}"]`)

      if (!existingPreload) {
        const link = document.createElement('link')
        link.rel = 'preload'
        link.as = 'image'
        link.href = url
        link.setAttribute('fetchpriority', 'high')
        document.head.appendChild(link)
      }
    })
  }, [currentTheme])

  return null
}
